/**
 * Typed API client for Workflow Localizations — per-(workflow template, locale)
 * translation bundles. Mirrors internal/wflocalizations.
 *
 * `translations` is a free-form key→string bundle (e.g. step/action labels)
 * that the workflow engine resolves at render time for the chosen locale.
 */

import { apiClient } from '@/lib/apiClient';

export type Translations = Record<string, unknown>;

/** Returns the saved translations bundle, or null when none exists yet (404). */
export async function getWorkflowLocalization(
  workflowTemplateId: string,
  locale: string,
): Promise<Translations | null> {
  try {
    const { data } = await apiClient.get<Translations>(
      `/workflows/localizations/${workflowTemplateId}/${locale}`,
    );
    return data;
  } catch (err: unknown) {
    if (
      err &&
      typeof err === 'object' &&
      'response' in err &&
      (err as { response?: { status?: number } }).response?.status === 404
    ) {
      return null;
    }
    throw err;
  }
}

export interface SetLocalizationRequest {
  workflowTemplateId: string;
  locale: string;
  translations: Translations;
}

export async function setWorkflowLocalization(req: SetLocalizationRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/workflows/localizations', req);
  return data;
}
